<template>
    <div class="view-report benchmark-report wiland-benchmark-report mx-3 mb-4" id="wiland-benchmark-page" ref="benchmarkPage" >
        <div class="row mx-0">
            <div :class="mainContentClass" class="p-0" id="top-of-page" >
                <div class="row mb-3" v-if="page == null">
                    <div class="col-12">
                        <div class="navbar navbar-expand bg-secondary rounded p-3">
                            <h4 class="mb-0 text-primary"><span class="d-inline-block spinner-border spinner-border-sm me-2" role="status"></span>
                            Your benchmark report is loading...</h4>
                        </div>
                    </div>
                </div>
                <div class="row mt-3 px-2" v-if="page != null" >
                    <!-- Top row: same order and styling as ReportView (T, Generate Slides, Filters) -->
                    <div v-if="currentView != 'dashboard'" class="col-12 mb-0 px-0">
                        <div class="d-flex justify-content-between align-items-center w-100">
                            <h2 class="mb-0">{{ headerTitle }}</h2>
                            <div class="d-flex align-items-center ms-2 mb-2">
                                <!-- Page-level Data Labels Toggle (T) - same as ReportView -->
                                <div class="chart-labels-toggle-container-page me-2">
                                    <button 
                                        class="btn btn-secondary labels-toggle-btn btn-icon-only-md" 
                                        @click="togglePageDataLabels" 
                                        data-bs-toggle="tooltip" 
                                        :title="page_show_data_labels ? 'Hide Data Labels' : 'Show Data Labels'"
                                        :class="{ 'active': page_show_data_labels }">
                                        <div class="toggle-knob">
                                            <img src="/img/icons/avid-icons/text.svg" alt="Toggle Labels" class="icon pe-0">
                                        </div>
                                    </button>
                                </div>
                                <!-- Generate Slides - same as ReportView -->
                                <div v-if="local_page != null && client != null && canExport('benchmarks')" class="me-2">
                                    <export-charts-button :page="local_page" :client="client" :user="user" @tab="setActiveTab"
                                        :is_filter_panel_expanded="is_filter_panel_expanded" :button_class="'btn-secondary'" :if_single_tab="true"></export-charts-button>
                                </div>
                                <div v-else-if="local_page != null && client != null && !canExport('benchmarks')" class="me-2" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                                    <button class="btn btn-secondary" :disabled="true" id="export-button">
                                        <img class="icon pe-0 mb-0" src="/img/icons/avid-icons/presention-chart.svg" alt="Presentation Icon">
                                        <span class="ps-1" v-if="!if_mobile">Generate Slides</span>
                                    </button>
                                </div>
                                <!-- Filters - same as ReportView (btn-primary, rightmost) -->
                                <button type="button" class="btn btn-primary text-nowrap" @click="toggleFilterPanel()">
                                    <img class="icon" src="/img/icons/avid-icons/filter-funnel-01.svg" alt="">
                                    <span class="ps-1" v-if="!is_filter_panel_expanded && !if_mobile">Filters ({{ filter_count }})</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Filters Applied row - only show when section view is active -->
                    <div class="col-12 mx-0 mb-3 px-0" v-if="currentView != 'dashboard' && applied_filters && applied_filters.length > 0">
                        <div class="filter-badge-container px-0">
                            <label>Filters Applied:</label>
                            <template v-if="applied_filters && applied_filters.length">
                                <template v-for="(filter, index) in applied_filters" :key="filter.filter_id + filter.filter_value">
                                    <div v-if="filter && filter.filter_id > 0" class="badge badge-white" :class="{'badge-with-btn': !filter.is_required}">
                                        <span v-if="filter.filter_type === 'date_range'" @click="navigateToFilter(filter)">
                                            {{ filter.filter_label }}: {{ dateRangeDisplay(filter.filter_value) }}
                                        </span>
                                        <span v-else-if="filter.filter_type === 'month_range'" @click="navigateToFilter(filter)">
                                            {{ filter.filter_label }}: {{ monthRangeDisplay(filter.filter_value) }}
                                        </span>
                                        <span v-else-if="filter.filter_type === 'single_select'" @click="navigateToFilter(filter)">
                                            {{ filter.filter_label }} = "{{ filter.filter_value }}"
                                        </span>
                                        <span v-else-if="filter.filter_type === 'multi_select' || filter.filter_type === 'month_picker' || filter.filter_type === 'array_single_select' || filter.filter_type === 'array_multi_select'" @click="navigateToFilter(filter)">
                                            {{ filter.filter_label }}{{ Array.isArray(filter.filter_value) && filter.filter_value.length > 1 ? ' (' + filter.filter_value.length + ')' : ' = "' + (Array.isArray(filter.filter_value) && filter.filter_value.length ? filter.filter_value[0] : filter.filter_value) + '"' }}
                                        </span>
                                        <span v-else-if="filter.filter_type === 'numeric_slider'" @click="navigateToFilter(filter)">
                                            {{ filter.filter_label }}
                                            <template v-if="Array.isArray(filter.filter_value) && filter.filter_value.length >= 2">
                                                between {{ filter.filter_value[0] }} and {{ filter.filter_value[1] }}
                                            </template>
                                        </span>
                                        <button v-if="!filter.is_required"
                                                class="btn btn-xs p-0 ps-1"
                                                @click.stop.prevent="removeAppliedFilter(filter)">
                                            <i class="fas fa-close"></i>
                                        </button>
                                    </div>
                                </template>
                            </template>
                        </div>
                    </div>

                    <!-- Dashboard: shown via BenchmarkViewWilandSection under same conditions as section views -->
                    <div v-if="currentView == 'dashboard'" class="col-12 px-0">
                        <benchmark-view-wiland-section
                            :user="user"
                            :client="client"
                            :connections="connections"
                            :section_id="null"
                            :initial_page="local_page"
                            :report_groups="report_groups"
                            :show_dashboard="true"
                            :dashboard_pages="dashboard_pages"
                            @goToSection="goToSection"
                            @goToViewAll="goToViewAll"
                        />
                    </div>

                    <!-- View All (full): render each section with BenchmarkViewWilandSection so user can scroll through all charts -->
                    <div v-else-if="currentView == 'full'" class="col-12 view-all-sections px-0">
                        <div v-for="(section, sectionIndex) in allSections" :key="section.id" class="view-all-section-block mb-5">
                            <!-- <h3 class="mb-4">{{ section.title }}</h3> -->
                            <benchmark-view-wiland-section
                                :user="user"
                                :client="client"
                                :connections="connections"
                                :section_id="section.id"
                                :initial_page="local_page"
                                :report_groups="report_groups"
                                :standalone="true"
                                :if_show_top_charts="sectionIndex === 0"
                                :page_show_data_labels="page_show_data_labels"
                                :if_refreshing="if_refreshing"
                                @navigateToFilter="navigateToFilter"
                                @removeAppliedFilter="removeAppliedFilter"
                            />
                        </div>
                    </div>

                    <div v-else class="col-12 px-0">
                        <benchmark-view-wiland-section :user="user" :client="client" :connections="connections" :section_id="currentView" :initial_page="local_page" :report_groups="report_groups" :page_show_data_labels="page_show_data_labels" :if_refreshing="if_refreshing" @navigateToFilter="navigateToFilter" @removeAppliedFilter="removeAppliedFilter" @goToDashboard="goToDashboard" />
                        
                    </div>

                    <benchmark-view-wiland-demo-cta :user="user" :client="client" />
                </div>
            </div>
        </div>

        <!-- Filter Panel Slide-in - Only show when section view is active -->
        <transition name="slide-fade" appear duration="500" v-if="currentView != 'dashboard'">
            <div class="filter-panel" :class="is_filter_panel_expanded && page != null ? 'open' : 'visually-hidden'">
                <div id="filter-panel--heading" class="p-4 pb-2">
                    <h3>Filters</h3>
                    <button type="button" class="btn btn-white float-end close-modal" @click="toggleFilterPanel()" aria-label="Close">
                        <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
                    </button>
                </div>
                <benchmark-filter-panel 
                    ref="filterPanel"
                    :page="local_page && local_page.dimensions ? local_page : (benchmark_page || page)" 
                    :benchmark_page="benchmark_page"
                    :wiland_page="wiland_page"
                    :client="client" 
                    :section_id="currentView" 
                    @refreshing="refreshing" 
                    @updatePage="updatePage"
                    @closePanel="toggleFilterPanel">
                </benchmark-filter-panel>
            </div>
        </transition>

        <support-modal-dynamic :user="user" :client="client" :selected_ticket_type="selected_ticket_type" :id="'support-modal-'+selected_ticket_type"></support-modal-dynamic>
    </div>
</template>

<script>
    import Swal from 'sweetalert2';
    import { defineComponent, ref, nextTick } from 'vue';
    import { Tooltip } from 'bootstrap';
    import permissionsMixin from '../../mixins/permissions';
    import { useNotifications } from '../../composables/useNotifications';
    import { useUserStore } from '../../stores/user';

    export default defineComponent({
        components: {
            Swal
        },
        props: {
            user: {
                type: [Object, null],
            },
            client: {
                type: [Object, null],
            },
            connections: {
                type: [Array, null],
            },
            section_id: {
                type: [Number, String, null],
            },
            dashboard_pages: {
                type: Array,
                default: () => []
            },
            report_groups: {
                type: Array,
                default: () => []
            }
        },
        mixins: [permissionsMixin],
        setup() {
            const benchmarkPage = ref(null);
            const reportSections = ref([]);
            const tooltips = ref([]);
            const { showNotification } = useNotifications();
            const userStore = useUserStore();

            return {
                benchmarkPage,
                reportSections,
                tooltips,
                showNotification,
                userStore
            };
        },
        data() {
            return {
                page: null,
                local_page: {},
                benchmark_page: null, // Store benchmark page separately for filter requests
                wiland_page: null, // Store wiland page separately for filter requests
                is_filter_panel_expanded: false,
                selected_ticket_type: '',
                sidebar_collapsed: localStorage.getItem('sidebar_collapsed') === 'true',
                if_mobile: window.innerWidth < 768,
                active_tab_index: 1,
                page_show_data_labels: (() => {
                    const stored = sessionStorage.getItem('insights_show_data_labels');
                    return stored !== null ? stored === 'true' : false;
                })(),
                currentView: null, // 'dashboard' or section id - which view to show
                if_refreshing: false, // true while filter apply is in progress so charts show loading state
            };
        },
        beforeMount() {
            this.currentView = (this.section_id == null || this.section_id === '') ? 'dashboard' : this.section_id;
            this.hydrateInitialShell();
            this.loadBenchmark();
        },
        mounted() {
            // Keep mobile flag reactive
            this.onResize = () => {
                this.if_mobile = window.innerWidth < 768;
            };
            window.addEventListener('resize', this.onResize);
            // Run once in case initial flag needs update after render
            this.onResize();
            
            window.addEventListener('storage', this.handleSidebarStorageChange);
            this.sidebar_collapsed = localStorage.getItem('sidebar_collapsed') === 'true';
            window.addEventListener('sidebar-toggle', this.handleSidebarToggleEvent);
            window.addEventListener('popstate', this.handlePopState);

            // Note: tooltips will be initialized via the page watcher once data loads
        },
        computed: {
            shouldShowSummary() {
                return this.user?.preferences?.show_ai_summaries !== false;
            },
            date_type() {
                if(this.page.data_blend_type == 'constituent')
                    return "Transaction ";
                else if(this.page.data_blend_type == 'subscriber')
                    return "Email ";
                return "";
            },
            sections() {
                return this.local_page.sections || [];
            },
            headerTitle() {
                if (this.currentView && this.currentView !== 'dashboard' && this.currentView !== 'full') {
                    const section = (this.local_page?.sections || []).find(s => s.id == this.currentView);
                    return section?.title ?? (this.local_page?.title || this.page?.title || 'Benchmark Report');
                }
                return this.local_page?.title || this.page?.title || 'Benchmark Report';
            },
            applied_filters() {
                if (!this.local_page || !Array.isArray(this.local_page.dimensions)) {
                    return [];
                }

                let applied_filters_list = [];

                for (var i = 0; i < this.local_page.dimensions.length; i++) {
                    let dim = this.local_page.dimensions[i];

                    const checkFilled = (val, fieldType) => {
                        if (!val) return false;
                        if (Array.isArray(val)) return val.length > 0;
                        if (typeof val === 'object') {
                            if (fieldType === 'date_range' || fieldType === 'month_range') {
                                return val.start_date || val.end_date;
                            }
                            return Object.keys(val).length > 0;
                        }
                        // primitive
                        return val !== '' && val !== null;
                    };

                    const hasValue = checkFilled(dim.default_value, dim.field_type) || checkFilled(dim.value, dim.field_type);

                    if (!hasValue) continue;

                    // Skip if multi-select style and ALL options are selected (default/unfiltered state)
                    if ((dim.field_type === 'multi_select' || dim.field_type === 'month_picker' || dim.field_type === 'array_single_select' || dim.field_type === 'array_multi_select') && Array.isArray(dim.value) && dim.options && dim.value.length === dim.options.length) {
                        continue;
                    }

                    // Vertical and Annual Revenue Range: only show in applied row when a subset is selected (not "all options" = unfiltered)
                    if (['org_vertical', 'org_annual_revenue'].includes(dim.column)) {
                        const allSelected = dim.options
                            ? (Array.isArray(dim.value) && dim.value.length >= dim.options.length)
                            : (Array.isArray(dim.value) && (
                                (dim.column === 'org_vertical' && dim.value.length >= 8) ||
                                (dim.column === 'org_annual_revenue' && dim.value.length >= 6)
                            ));
                        if (allSelected) {
                            continue;
                        }
                    }

                    //hide Time Period filter from badges and filter count
                    if (dim.field_type === 'single_select') {
                        const singleSelectOptionCount = (dim.options && dim.options.length) ? dim.options.length : (dim.column === 'period_type' ? 2 : 0);
                        if (singleSelectOptionCount === 2) {
                            continue;
                        }
                    }

                    const isDonation = (val) => Array.isArray(val) ? (val.length === 1 && val[0] === 'Donation') : false;
                    if (!isDonation(dim.default_value) && !isDonation(dim.value)) {
                        applied_filters_list.push({
                            filter_id: dim.id,
                            filter_label: dim.label,
                            filter_value: (dim.value != null ? dim.value : dim.default_value),
                            filter_default_value: ((typeof dim.column === 'string' || dim.column instanceof String) && dim.column.indexOf('time_period.year_type') !== -1 ? this.client?.default_reporting_period : dim.default_value),
                            filter_column_name: dim.column,
                            filter_type: dim.field_type,
                            is_required: dim.is_required
                        })
                    }
                }
                return applied_filters_list;
            },
            filter_count() {
                return this.applied_filters.length;
            },
            allSections() {
                const sections = this.local_page?.sections || [];
                return sections.filter((_, index) => index !== 0);
            },
            mainContentClass() {
                return {
                    'has-filter-panel-open': this.is_filter_panel_expanded,
                    'has-filter-panel-closed': !this.is_filter_panel_expanded,
                    'sidebar-collapsed': this.sidebar_collapsed,
                    'sidebar-expanded': !this.sidebar_collapsed,
                };
            },
        },
        watch: {
            section_id: {
                handler(newVal) {
                    this.currentView = (newVal == null || newVal === '') ? 'dashboard' : newVal;
                    if (this.section_id > 0) {
                        var index = this.local_page.sections.findIndex(section => section.id == this.section_id);
                        if (index >= 0) {
                            this.setActiveTab(index);
                        } else {
                            this.setActiveTab(0);
                        }
                    }
                }
            },
            currentView() {
                this.is_filter_panel_expanded = false;
            },
            page: {
                handler(newValue) {
                    if (newValue) {
                        nextTick(() => {
                            this.initializeTooltips();
                        });
                    }
                }
            }
        },
        methods: {
            buildCombinedSectionsFromDashboardPages() {
                if (!Array.isArray(this.dashboard_pages) || this.dashboard_pages.length === 0) {
                    return [];
                }

                const combinedSections = [{
                    id: 0,
                    title: '',
                    is_section_tab_active: false
                }];

                this.dashboard_pages.forEach((dashboardPage) => {
                    if (!dashboardPage || !Array.isArray(dashboardPage.sections)) {
                        return;
                    }

                    dashboardPage.sections.forEach((section) => {
                        combinedSections.push({
                            ...section,
                            is_section_tab_active: false
                        });
                    });
                });

                return combinedSections;
            },
            hydrateInitialShell() {
                const combinedSections = this.buildCombinedSectionsFromDashboardPages();

                if (combinedSections.length === 0) {
                    return false;
                }

                this.page = {
                    sections: combinedSections
                };
                this.local_page = JSON.parse(JSON.stringify(this.page));
                this.loadSectionTab();

                if (this.section_id > 0) {
                    const index = this.local_page.sections.findIndex(section => section.id == this.section_id);
                    this.setActiveTab(index >= 0 ? index : 1);
                }

                return true;
            },
            loadBenchmark() {
                var self = this;
                let benchmarkUrl;
                let wilandBenchmarkUrl;
                
                if(this.client == null) {
                    benchmarkUrl = '/api/benchmarks/demo/10294/charts';
                    wilandBenchmarkUrl = '/api/wiland_benchmarks/demo/10123/charts';
                } else {
                    benchmarkUrl = '/api/benchmarks/' + this.client.url + "/10294/charts";
                    wilandBenchmarkUrl = '/api/wiland_benchmarks/' + this.client.url + "/10123/charts";
                }
                
                if (this.section_id > 0) {
                    benchmarkUrl += '/' + this.section_id;
                    wilandBenchmarkUrl += '/' + this.section_id;
                }

                // Make both API calls in parallel
                Promise.all([
                    window.axios.get(benchmarkUrl),
                    window.axios.get(wilandBenchmarkUrl)
                ])
                    .then(([benchmarkResponse, wilandResponse]) => {
                        // Use the wiland benchmark response as the primary data source
                        const benchmarkPage = benchmarkResponse.data.page;
                        const wilandPage = wilandResponse.data.page;
                        
                        // Store both pages separately for filter requests
                        self.benchmark_page = JSON.parse(JSON.stringify(benchmarkPage));
                        self.wiland_page = JSON.parse(JSON.stringify(wilandPage));
                        
                        // Start with wiland page as base
                        self.page = JSON.parse(JSON.stringify(wilandPage));
                        self.local_page = JSON.parse(JSON.stringify(wilandPage));
                        
                        // Combine sections from both pages
                        // Index 0 is a placeholder (not displayed in template due to v-if="index!=0")
                        const combinedSections = [];
                        
                        // Add placeholder section at index 0 (not displayed, but needed for array structure)
                        combinedSections.push({
                            id: 0,
                            title: '',
                            is_section_tab_active: false
                        });
                        
                        // Add all sections from wiland response first
                        if (wilandPage.sections && wilandPage.sections.length > 0) {
                            wilandPage.sections.forEach(section => {
                                combinedSections.push({
                                    ...section,
                                    is_section_tab_active: false
                                });
                            });
                        }
                        
                        // Add all sections from benchmark response second
                        if (benchmarkPage.sections && benchmarkPage.sections.length > 0) {
                            benchmarkPage.sections.forEach(section => {
                                combinedSections.push({
                                    ...section,
                                    is_section_tab_active: false
                                });
                            });
                        }
                        
                        // Update both page and local_page with combined sections
                        self.local_page.sections = combinedSections;
                        self.page.sections = combinedSections;
                        
                        // Always use benchmark dimensions for filters (wiland doesn't have filters)
                        if (benchmarkPage.dimensions && benchmarkPage.dimensions.length > 0) {
                            self.local_page.dimensions = benchmarkPage.dimensions;
                            self.page.dimensions = benchmarkPage.dimensions;
                        }
                        
                        self.setDefaultFilterValues();
                        const hasPersistedFilters = self.loadPersistedFilters();
                        self.loadSectionTab();

                        if (hasPersistedFilters) {
                            // Set refreshing flag so charts show loading overlay
                            self.if_refreshing = true;
                            self.$nextTick(() => {
                                self.applyPersistedFilters();
                            });
                        } else if (self.section_id > 0) {
                            var index = self.local_page.sections.findIndex(section => section.id == self.section_id);
                            if (index >= 0) {
                                self.setActiveTab(index);
                            } else {
                                self.setActiveTab(1);
                            }
                        }
                    })
                    .catch(error => {
                        console.error('Error loading benchmark:', error);
                    });
            },
            toggleFilterPanel(){
                this.is_filter_panel_expanded = !this.is_filter_panel_expanded
            },
            setDefaultFilterValues() {
                if (this.local_page.dimensions) {
                    for (var i = 0; i < this.local_page.dimensions.length; i++) {
                        let dim = this.local_page.dimensions[i];
                        if(dim.field_type == "multi_select" || dim.field_type == "array_single_select" || dim.field_type == "array_multi_select") {
                            this.local_page.dimensions[i].value = [];
                        } else if(dim.field_type == "single_select") {
                            // For period type default to Trailing 12 Months
                            if (dim.column === 'period_type') {
                                this.local_page.dimensions[i].value = 'Trailing 12 Months';
                            } else {
                                this.local_page.dimensions[i].value = "";
                            }
                        }
                    }
                }
            },
            saveFiltersToStorage() {
                if (!this.local_page || !this.local_page.dimensions) {
                    return;
                }
                
                const filterState = {};
                this.local_page.dimensions.forEach(dim => {
                    if (dim.value !== null && dim.value !== undefined) {
                        filterState[dim.column] = dim.value;
                    }
                });
                
                const clientKey = this.client ? this.client.url : 'demo';
                const storageKey = `wiland_benchmark_filters_${clientKey}`;
                
                try {
                    localStorage.setItem(storageKey, JSON.stringify(filterState));
                } catch (e) {
                    console.error('Failed to save filters to localStorage:', e);
                }
            },
            loadPersistedFilters() {
                if (!this.local_page || !this.local_page.dimensions) {
                    return false;
                }
                
                const clientKey = this.client ? this.client.url : 'demo';
                const storageKey = `wiland_benchmark_filters_${clientKey}`;
                
                try {
                    const savedFilters = localStorage.getItem(storageKey);
                    if (!savedFilters) {
                        return false;
                    }
                    
                    const filterState = JSON.parse(savedFilters);
                    let filtersApplied = false;
                    
                    this.local_page.dimensions.forEach((dim, index) => {
                        if (filterState.hasOwnProperty(dim.column)) {
                            this.local_page.dimensions[index].value = filterState[dim.column];
                            filtersApplied = true;
                        }
                    });
                    
                    return filtersApplied;
                } catch (e) {
                    console.error('Failed to load persisted filters:', e);
                    return false;
                }
            },
            applyPersistedFilters() {
                const applyToBothPages = this.benchmark_page && this.wiland_page;
                
                if (!applyToBothPages) {
                    this.if_refreshing = false;
                    return;
                }

                // Make a copy of the page with persisted filters
                const page_copy = JSON.parse(JSON.stringify(this.local_page));
                
                // Clear chart data
                if (page_copy.sections && Array.isArray(page_copy.sections)) {
                    page_copy.sections.forEach(section => {
                        if (section.charts && Array.isArray(section.charts)) {
                            section.charts.forEach(chart => {
                                if (chart.chart) {
                                    chart.chart.data = null;
                                }
                            });
                        }
                    });
                }

                const benchmarkPageCopy = JSON.parse(JSON.stringify(this.benchmark_page));
                benchmarkPageCopy.dimensions = page_copy.dimensions;
                
                if (benchmarkPageCopy.sections && Array.isArray(benchmarkPageCopy.sections)) {
                    benchmarkPageCopy.sections.forEach(section => {
                        if (section.charts && Array.isArray(section.charts)) {
                            section.charts.forEach(chart => {
                                if (chart.chart) {
                                    chart.chart.data = null;
                                }
                            });
                        }
                    });
                }

                const wilandPageCopy = JSON.parse(JSON.stringify(this.wiland_page));
                wilandPageCopy.dimensions = page_copy.dimensions;
                
                if (wilandPageCopy.sections && Array.isArray(wilandPageCopy.sections)) {
                    wilandPageCopy.sections.forEach(section => {
                        if (section.charts && Array.isArray(section.charts)) {
                            section.charts.forEach(chart => {
                                if (chart.chart) {
                                    chart.chart.data = null;
                                }
                            });
                        }
                    });
                }

                // Fetch filtered data
                Promise.all([
                    window.axios.post('/api/insights/page/' + this.benchmark_page.id, benchmarkPageCopy),
                    window.axios.post('/api/insights/page/' + this.wiland_page.id, wilandPageCopy)
                ])
                    .then(([benchmarkResponse, wilandResponse]) => {
                        const benchmarkPageData = benchmarkResponse.data.page;
                        const wilandPageData = wilandResponse.data.page;
                        
                        this.benchmark_page = benchmarkPageData;
                        this.wiland_page = wilandPageData;
                        
                        const combinedSections = [];
                        
                        combinedSections.push({
                            id: 0,
                            title: '',
                            is_section_tab_active: false
                        });
                        
                        if (wilandPageData && wilandPageData.sections && wilandPageData.sections.length > 0) {
                            wilandPageData.sections.forEach(section => {
                                combinedSections.push({
                                    ...section,
                                    is_section_tab_active: false
                                });
                            });
                        }
                        
                        if (benchmarkPageData && benchmarkPageData.sections && benchmarkPageData.sections.length > 0) {
                            benchmarkPageData.sections.forEach(section => {
                                combinedSections.push({
                                    ...section,
                                    is_section_tab_active: false
                                });
                            });
                        }
                        
                        this.local_page = {
                            ...benchmarkPageData,
                            sections: combinedSections
                        };
                        
                        // Keep the persisted dimensions
                        this.local_page.dimensions = page_copy.dimensions;
                        
                        this.page = {
                            ...this.page,
                            sections: combinedSections
                        };
                        
                        this.if_refreshing = false;
                        this.setActiveTab(this.active_tab_index);
                    })
                    .catch(error => {
                        console.error('Error applying persisted filters:', error);
                        this.if_refreshing = false;
                    });
            },
            clearPersistedFilters() {
                const clientKey = this.client ? this.client.url : 'demo';
                const storageKey = `wiland_benchmark_filters_${clientKey}`;
                
                try {
                    localStorage.removeItem(storageKey);
                } catch (e) {
                    console.error('Failed to clear persisted filters:', e);
                }
            },
            loadSectionTab() {
                for(var i = 0; i < this.local_page.sections.length; i++) {
                    if(i == 1) {
                        this.local_page.sections[i].is_section_tab_active = true;
                    } else {
                        this.local_page.sections[i].is_section_tab_active = false;
                    }
                }
            },
            initializeTooltips() {
                // Destroy existing tooltips first
                this.tooltips.forEach(tooltip => {
                    try {
                        tooltip.dispose();
                    } catch (e) {
                        // ignore if already disposed
                    }
                });
                this.tooltips = [];

                // Initialize new tooltips only when root ref and elements are in DOM
                if (!this.benchmarkPage) return;
                const tooltipElements = this.benchmarkPage.querySelectorAll('[data-bs-toggle="tooltip"]');
                tooltipElements.forEach(element => {
                    if (!element || !document.contains(element)) return;
                    try {
                        const tooltip = new Tooltip(element);
                        this.tooltips.push(tooltip);
                    } catch (e) {
                        // Bootstrap Tooltip can throw if element is detached or invalid
                    }
                });
            },
            setActiveTab(index) {
                if(index == 0) return;

                this.is_filter_panel_expanded = false;

                // Update tab states
                for(var i = 0; i < this.local_page.sections.length; i++) {
                    if(i == index) {
                        this.local_page.sections[i].is_section_tab_active = true;
                    } else {
                        this.local_page.sections[i].is_section_tab_active = false;
                    }
                }
                // Use nextTick to ensure DOM is updated before reinitializing tooltips
                nextTick(() => {
                    this.initializeTooltips();
                });
            },
            refreshing(if_refreshing) {
                this.if_refreshing = if_refreshing;

                // Refreshing notification - no need to show, just close any existing
                if(if_refreshing) {
                    Swal.close();
                }
            },
            updatePage(updatedPages) {
                this.if_refreshing = false;
                try {
                    // updatedPages can be an object with benchmark_page and wiland_page, or a single page
                    let benchmarkPageData, wilandPageData;
                    
                    if (updatedPages.benchmark_page && updatedPages.wiland_page) {
                        // Both pages were updated
                        benchmarkPageData = updatedPages.benchmark_page;
                        wilandPageData = updatedPages.wiland_page;
                        
                        // Update stored pages
                        this.benchmark_page = benchmarkPageData;
                        this.wiland_page = wilandPageData;
                    } else {
                        // Single page update (backward compatibility)
                        benchmarkPageData = updatedPages;
                        wilandPageData = this.wiland_page;
                    }
                    
                    // Combine sections from both updated pages
                    const combinedSections = [];
                    
                    // Add placeholder section at index 0
                    combinedSections.push({
                        id: 0,
                        title: '',
                        is_section_tab_active: false
                    });
                    
                    // Add all sections from wiland page first
                    if (wilandPageData && wilandPageData.sections && wilandPageData.sections.length > 0) {
                        wilandPageData.sections.forEach(section => {
                            combinedSections.push({
                                ...section,
                                is_section_tab_active: false
                            });
                        });
                    }
                    
                    // Add all sections from benchmark page second
                    if (benchmarkPageData && benchmarkPageData.sections && benchmarkPageData.sections.length > 0) {
                        benchmarkPageData.sections.forEach(section => {
                            combinedSections.push({
                                ...section,
                                is_section_tab_active: false
                            });
                        });
                    }
                    
                    // Update local_page with combined sections and dimensions from benchmark
                    this.local_page = {
                        ...benchmarkPageData,
                        sections: combinedSections
                    };
                    
                    // Use applied dimensions from filter panel when present so filter count stays correct
                    if (updatedPages.dimensions && Array.isArray(updatedPages.dimensions)) {
                        this.local_page.dimensions = updatedPages.dimensions;
                    } else if (benchmarkPageData.dimensions) {
                        this.local_page.dimensions = benchmarkPageData.dimensions;
                    }
                    
                    // Update page object as well
                    this.page = {
                        ...this.page,
                        sections: combinedSections
                    };
                    
                    this.setActiveTab(this.active_tab_index);

                    Swal.close();

                    this.showNotification({
                        text: 'The benchmark data has been refreshed!'
                    });
                } catch (error) {
                    console.error('Error updating page:', error);
                    Swal.fire({
                        title: 'Error',
                        text: 'Something went wrong while updating the page. Please try again.',
                        icon: 'error',
                        confirmButtonText: "OK",
                        customClass: {
                            popup: 'swal-error'
                        },
                    });
                }
            },
            goToNextSection() {
                var new_index = this.active_tab_index + 1;
                this.setActiveTab(new_index);
                document.getElementById('top-of-page').scrollIntoView({ behavior: 'auto', block: 'start' });
            },
            goToDashboard() {
                this.currentView = 'dashboard';
                const url = '/benchmarks/' + (this.client ? this.client.url : 'demo') + '/dashboard';
                window.history.pushState({ currentView: 'dashboard' }, '', url);
                this.$nextTick(() => window.scrollTo(0, 0));
            },
            goToViewAll() {
                this.currentView = 'full';
                const url = '/benchmarks/' + (this.client ? this.client.url : 'demo') + '/full';
                window.history.pushState({ currentView: 'full' }, '', url);
                this.scrollToTop();
            },
            goToSection(sectionId) {
                this.currentView = sectionId;
                const url = '/benchmarks/' + (this.client ? this.client.url : 'demo') + '/' + sectionId;
                window.history.pushState({ currentView: sectionId }, '', url);
                this.scrollToTop();
            },
            toggleFilterPanel(){
                this.is_filter_panel_expanded = !this.is_filter_panel_expanded
            },
            togglePageDataLabels(event) {
                const newPageValue = !this.page_show_data_labels;
                this.page_show_data_labels = newPageValue;
                sessionStorage.setItem('insights_show_data_labels', String(newPageValue));

                // Update all charts in all sections via BenchmarkViewWilandSection
                // The BenchmarkViewWilandSection component will handle updating its report-view-section components
                this.$nextTick(() => {
                    // Hide tooltip
                    if (event && event.currentTarget) {
                        const button = event.currentTarget;
                        if (window.bootstrap && window.bootstrap.Tooltip) {
                            const tooltip = window.bootstrap.Tooltip.getInstance(button);
                            if (tooltip) {
                                tooltip.hide();
                            }
                        }
                        button.blur();
                    }
                });
            },
            navigateToFilter(filter) {
                // open filter drawer if closed
                if (!this.is_filter_panel_expanded) {
                    this.toggleFilterPanel();
                }

                // wait DOM update then delegate to child component
                this.$nextTick(() => {
                    if (this.$refs.filterPanel && typeof this.$refs.filterPanel.navigateToFilter === 'function') {
                        this.$refs.filterPanel.navigateToFilter(filter);
                    }
                });
            },
            async sha256(message) {
                const msgBuffer = new TextEncoder().encode(message);
                const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
            },
            arraysAreEqual(a, b) {
                if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
                const keysA = Object.keys(a).sort();
                const keysB = Object.keys(b).sort();
                if (keysA.length !== keysB.length) return false;
                for (let i = 0; i < keysA.length; i++) {
                    if (keysA[i] !== keysB[i]) return false;
                    if (typeof a[keysA[i]] === 'object' && a[keysA[i]] !== null && b[keysB[i]] !== null && !Array.isArray(a[keysA[i]])) {
                        if (!this.arraysAreEqual(a[keysA[i]], b[keysB[i]])) return false;
                    } else if (Array.isArray(a[keysA[i]]) && Array.isArray(b[keysB[i]])) {
                        if (!this.arraysAreEqual(a[keysA[i]], b[keysB[i]])) return false;
                    } else if (a[keysA[i]] !== b[keysB[i]]) return false;
                }
                return true;
            },
            async buildFilterObjFromDimensions(dimensions) {
                const filterObj = {};
                if (!dimensions || !dimensions.length) return filterObj;
                for (const dim of dimensions) {
                    const defaultValue = (typeof dim.column === 'string' || dim.column instanceof String) && dim.column.indexOf('time_period.year_type') !== -1
                        ? this.client?.default_reporting_period : dim.default_value;
                    const hasValue = (dim.field_type === 'date_range' || dim.field_type === 'month_range')
                        ? (dim.value && (dim.value.start_date || dim.value.end_date))
                        : (dim.value && (Array.isArray(dim.value) ? dim.value.length > 0 : dim.value !== '' && dim.value != null));
                    if (!hasValue) continue;
                    if (defaultValue != null && this.arraysAreEqual(defaultValue, dim.value)) continue;
                    let colName = (dim.column || '').toString().toLowerCase()
                        .replace(/(\r\n|\n|\r)/g, ' ')
                        .replace(/\s\s+/g, ' ');
                    if (!colName) continue;
                    const hashedColumnName = await this.sha256(colName);
                    filterObj[hashedColumnName] = dim.value;
                }
                return filterObj;
            },
            updateURL() {
                // Filter values are no longer stored in the URL
            },
            dateRangeDisplay(value) {
                if (!value) return '';
                if (value.start_date && value.end_date) {
                    return this.formatUtcDate(value.start_date) + ' - ' + this.formatUtcDate(value.end_date);
                } else if (value.start_date) {
                    return 'On or After ' + this.formatUtcDate(value.start_date);
                } else if (value.end_date) {
                    return 'Prior to ' + this.formatUtcDate(value.end_date);
                }
                return '';
            },
            monthRangeDisplay(value) {
                if (!value) return '';
                if (value.start_date && value.end_date) {
                    return this.formatUtcMonth(value.start_date) + ' - ' + this.formatUtcMonth(value.end_date);
                } else if (value.start_date) {
                    return 'On or After ' + this.formatUtcMonth(value.start_date);
                } else if (value.end_date) {
                    return 'Prior to ' + this.formatUtcMonth(value.end_date);
                }
                return '';
            },
            formatUtcDate(dateString) {
                if (!dateString) return '';
                const date = new Date(dateString);
                const offset = date.getTimezoneOffset();
                date.setMinutes(date.getMinutes() + offset);
                const mm = String(date.getMonth() + 1).padStart(2, '0');
                const dd = String(date.getDate()).padStart(2, '0');
                const yyyy = date.getFullYear();
                return `${mm}/${dd}/${yyyy}`;
            },
            formatUtcMonth(dateString) {
                if (!dateString) return '';
                const date = new Date(dateString);
                const offset = date.getTimezoneOffset();
                date.setMinutes(date.getMinutes() + offset);
                const month = date.toLocaleString('default', { month: 'short' });
                const year = date.getFullYear();
                return `${month} ${year}`;
            },
            removeAppliedFilter(filter) {
                if (!filter) return;

                // Reset value in local_page dimensions
                const dimIndex = this.local_page.dimensions.findIndex(d => d.id == filter.filter_id);
                if (dimIndex < 0) return;

                const dim = this.local_page.dimensions[dimIndex];

                if (['org_vertical','org_annual_revenue'].includes(dim.column)) {
                    // Default for benchmark filters → select all
                    dim.value = [...(dim.options || [])];
                } else if (dim.field_type === 'single_select') {
                    dim.value = '';
                } else if (dim.field_type === 'multi_select' || dim.field_type === 'month_picker' || dim.field_type === 'array_single_select' || dim.field_type === 'array_multi_select') {
                    dim.value = [];
                } else if (dim.field_type === 'numeric_slider') {
                    dim.value = null;
                } else if (dim.field_type === 'date_range' || dim.field_type === 'month_range') {
                    dim.value = { start_date: null, end_date: null };
                }

                // Ask filter panel to sync & apply
                this.$nextTick(() => {
                    if (this.$refs.filterPanel) {
                        if (typeof this.$refs.filterPanel.checkAndApplyFilters === 'function') {
                            this.$refs.filterPanel.checkAndApplyFilters();
                        }
                        if (typeof this.$refs.filterPanel.applyFilter === 'function') {
                            this.$refs.filterPanel.applyFilter();
                        }
                    }
                });
            },
            openSupportModal() {
                this.selected_ticket_type = 'FEATURE_REQUEST';
            },
            handleSidebarStorageChange(e) {
                if (e.key === 'sidebar_collapsed') {
                    this.sidebar_collapsed = e.newValue === 'true';
                }
            },
            handleSidebarToggleEvent() {
                this.sidebar_collapsed = localStorage.getItem('sidebar_collapsed') === 'true';
            },
            scrollToTop() {
                const doScroll = () => {
                    const el = document.getElementById('top-of-page');
                    if (el) {
                        el.scrollIntoView({ behavior: 'auto', block: 'start' });
                    } else {
                        window.scrollTo(0, 0);
                    }
                };
                doScroll();
                this.$nextTick(() => doScroll());
                setTimeout(doScroll, 150);
            },
            handlePopState(event) {
                if (event.state && event.state.currentView !== undefined) {
                    this.currentView = event.state.currentView;
                } else {
                    // Back/forward to initial load or external link - derive view from URL
                    const path = window.location.pathname;
                    const segments = path.split('/').filter(Boolean);
                    if (segments[segments.length - 1] === 'dashboard') {
                        this.currentView = 'dashboard';
                    } else if (segments[segments.length - 1] === 'full') {
                        this.currentView = 'full';
                    } else {
                        const last = segments[segments.length - 1];
                        const n = parseInt(last, 10);
                        this.currentView = Number.isInteger(n) ? n : 'dashboard';
                    }
                }
            },
        },
        beforeUnmount() {
            // Clean up tooltips
            this.tooltips.forEach(tooltip => {
                tooltip.dispose();
            });
            
            // Clean up event listeners
            if (this.onResize) {
                window.removeEventListener('resize', this.onResize);
            }
            window.removeEventListener('storage', this.handleSidebarStorageChange);
            window.removeEventListener('sidebar-toggle', this.handleSidebarToggleEvent);
            window.removeEventListener('popstate', this.handlePopState);
        }
    });
</script>